(() => {
  'use strict';

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Mobile menu ---------- */
  const mobileMenu = qs('[data-mobile-menu]');
  const menuToggle = qs('[data-mobile-menu-toggle]');

  function setMenuOpen(open) {
    if (!mobileMenu || !menuToggle) return;
    mobileMenu.hidden = !open;
    menuToggle.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('mobile-menu-open', open);
  }

  menuToggle?.addEventListener('click', () => setMenuOpen(mobileMenu.hidden));
  qsa('[data-mobile-menu-close]').forEach((el) => {
    el.addEventListener('click', () => setMenuOpen(false));
  });

  /* ---------- Carousels ---------- */
  qsa('[data-featured-collection]').forEach((section) => {
    const track = qs('[data-carousel]', section);
    if (!track) return;

    const prevBtn = qs('[data-carousel-prev]', section);
    const nextBtn = qs('[data-carousel-next]', section);

    const scrollByCard = (dir) => {
      const card = qs('[data-carousel-item]', track);
      const amount = card ? card.getBoundingClientRect().width + 16 : 280;
      track.scrollBy({ left: dir * amount, behavior: 'smooth' });
    };

    prevBtn?.addEventListener('click', () => scrollByCard(-1));
    nextBtn?.addEventListener('click', () => scrollByCard(1));
  });

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-qty-minus], button[name="minus"]');
    const plus = e.target.closest('[data-qty-plus], button[name="plus"]');
    if (!minus && !plus) return;

    const wrap = (minus || plus).closest('.qty-selector, .cart-item__qty, .cart-table__qty');
    const input = wrap && qs('input[type="number"]', wrap);
    if (!input) return;

    const current = parseInt(input.value, 10) || 0;
    const min = parseInt(input.min || '0', 10);
    input.value = plus ? current + 1 : Math.max(min, current - 1);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Collection filters ---------- */
  const filterRoot = qs('[data-collection-filters]');
  if (filterRoot) {
    const applyBtn = qs('[data-filter-apply]', filterRoot);
    const sortSelect = qs('[data-sort-by]', filterRoot);

    const buildFilterUrl = () => {
      const url = new URL(window.location.href);
      url.search = '';

      qsa('[data-filter-input]', filterRoot).forEach((input) => {
        if (input.type === 'checkbox') {
          if (input.checked) url.searchParams.append(input.name, input.value);
        } else if (input.value !== '') {
          url.searchParams.set(input.name, input.value);
        }
      });

      if (sortSelect?.value) {
        url.searchParams.set('sort_by', sortSelect.value);
      }

      return url.toString();
    };

    applyBtn?.addEventListener('click', () => {
      window.location.href = buildFilterUrl();
    });

    sortSelect?.addEventListener('change', () => {
      window.location.href = buildFilterUrl();
    });
  }

  /* ---------- Predictive search ---------- */
  const searchForm = qs('[data-predictive-search]');
  if (searchForm && window.Shopify) {
    const input = qs('[data-search-input]', searchForm);
    const results = qs('[data-predictive-results]', searchForm.closest('[data-search-details]') || document);
    let timer;

    input?.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (!results) return;
      if (q.length < 2) {
        results.hidden = true;
        results.innerHTML = '';
        return;
      }

      timer = setTimeout(async () => {
        try {
          const res = await fetch(
            `${window.Shopify.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`
          );
          const data = await res.json();
          const products = data.resources?.results?.products || [];

          if (!products.length) {
            results.innerHTML = '<p class="predictive-search__empty">No results</p>';
            results.hidden = false;
            return;
          }

          results.innerHTML = products
            .map(
              (p) => `
              <a class="predictive-search__item" href="${p.url}">
                <img src="${p.image || ''}" alt="" width="56" height="70" loading="lazy">
                <span>
                  <strong>${p.title}</strong><br>
                  <span>${p.price}</span>
                </span>
              </a>`
            )
            .join('');
          results.hidden = false;
        } catch (err) {
          console.warn('Predictive search failed', err);
        }
      }, 250);
    });
  }

  /* ---------- Product gallery thumbs ---------- */
  const gallery = qs('[data-product-gallery]');
  if (gallery) {
    const mainImg = qs('#ProductMainImage', gallery);
    qsa('[data-thumb]', gallery).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        qsa('[data-thumb]', gallery).forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        if (mainImg && thumb.dataset.mediaUrl) {
          mainImg.src = thumb.dataset.mediaUrl;
          mainImg.srcset = '';
        }
      });
    });
  }

  /* ---------- Product variant selection ---------- */
  const productSection = qs('[data-product-section]');
  if (productSection) {
    const jsonEl = qs('[data-product-json]', productSection);
    const form = qs('[data-product-form]', productSection);
    if (jsonEl && form) {
      const product = JSON.parse(jsonEl.textContent);
      const variantInput = qs('[data-variant-id]', form);
      const addBtn = qs('[data-add-to-cart]', form);
      const priceEl = qs('[data-product-price]', productSection);
      const optionInputs = qsa('[data-option-input]', form);

      const getSelectedOptions = () =>
        optionInputs
          .filter((input) => input.checked)
          .sort((a, b) => {
            const ai = parseInt(a.closest('[data-option-index]')?.dataset.optionIndex || '0', 10);
            const bi = parseInt(b.closest('[data-option-index]')?.dataset.optionIndex || '0', 10);
            return ai - bi;
          })
          .map((input) => input.value);

      const findVariant = () => {
        const selected = getSelectedOptions();
        return product.variants.find((v) =>
          v.options.every((opt, i) => opt === selected[i])
        );
      };

      const formatMoney = (cents) => {
        try {
          return Shopify.formatMoney
            ? Shopify.formatMoney(cents)
            : (cents / 100).toLocaleString(undefined, { style: 'currency', currency: Shopify.currency?.active || 'AUD' });
        } catch {
          return `$${(cents / 100).toFixed(2)}`;
        }
      };

      const updateUI = () => {
        const variant = findVariant();
        if (!variant) {
          if (addBtn) {
            addBtn.disabled = true;
            addBtn.textContent = addBtn.dataset.unavailableText || 'Unavailable';
          }
          return;
        }

        if (variantInput) variantInput.value = variant.id;

        optionInputs.forEach((input) => {
          if (input.checked) {
            const label = input.closest('fieldset')?.querySelector('[data-option-value]');
            if (label) label.textContent = input.value;
          }
        });

        if (priceEl) {
          const onSale = variant.compare_at_price > variant.price;
          priceEl.innerHTML = `
            <div class="price${onSale ? ' price--on-sale' : ''}">
              <div class="price__regular">
                <span class="price-item price-item--regular">${formatMoney(variant.price)}</span>
              </div>
              <div class="price__sale">
                <span class="price-item price-item--sale">${formatMoney(variant.price)}</span>
                <s class="price-item price-item--compare">${formatMoney(variant.compare_at_price)}</s>
              </div>
            </div>`;
        }

        if (addBtn) {
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available ? 'Add to bag' : 'Sold out';
        }

        if (variant.featured_media && gallery) {
          const thumb = qs(`[data-media-id="${variant.featured_media.id}"]`, gallery);
          thumb?.click();
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };

      optionInputs.forEach((input) => input.addEventListener('change', updateUI));
    }
  }

  /* ---------- Product recommendations ---------- */
  const rec = qs('[data-product-recommendations]');
  if (rec?.dataset.url) {
    fetch(rec.dataset.url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('[data-product-recommendations]');
        if (section && section.innerHTML.trim()) {
          rec.innerHTML = section.innerHTML;
        }
      })
      .catch(() => {});
  }

  /* ---------- Close search on outside click ---------- */
  document.addEventListener('click', (e) => {
    qsa('[data-search-details][open]').forEach((details) => {
      if (!details.contains(e.target)) details.removeAttribute('open');
    });
  });
})();
